/**
 * Step 1: search for the organization to add. The search text lives in the ?q=
 * query string so going back from step 2 returns to the same results. A
 * organization without a card payment platform cannot get an event campaign, so it
 * is shown but not selectable.
 */

import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Button,
  Form,
  Input,
  Label,
  Message,
  Segment,
  Table
} from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { searchOrganizations } from '../api';
import { i18nKeys, MIN_SEARCH_LENGTH, SEARCH_DEBOUNCE_MS } from '../constants';
import {
  organizationEventsPath,
  formatOrganizationLocation,
  searchReturnState
} from '../helpers';
import { OrganizationSearchResult } from '../types';
import styles from '../AddOrganizationToEvents.module.css';

const SEARCH_PARAM = 'q';

const OrganizationSearch: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [text, setText] = useState(searchParams.get(SEARCH_PARAM) ?? '');
  // Kept with the term they answer, so older results can be marked stale.
  const [found, setFound] = useState<{
    term: string;
    results: OrganizationSearchResult[];
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const term = text.trim();
  const results = found?.results ?? null;
  // True from the keystroke until the new term's response arrives, covering
  // both the debounce wait and a slow request.
  const isStale = found !== null && found.term !== term;

  useEffect(() => {
    if (term.length < MIN_SEARCH_LENGTH) {
      // A request this change cancels leaves loading set (its finally sees isCurrent false), so clear it here.
      setFound(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    // Ignore responses for a term the user has already typed past.
    let isCurrent = true;
    const timer = setTimeout(async () => {
      setIsLoading(true);
      setError(null);
      try {
        const matches = await searchOrganizations(term);
        if (isCurrent) {
          setFound({ term, results: matches });
        }
      } catch {
        if (isCurrent) {
          setFound(null);
          setError(I18n.t(i18nKeys.SEARCH.FAILED));
        }
      } finally {
        if (isCurrent) {
          setIsLoading(false);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      isCurrent = false;
      clearTimeout(timer);
    };
  }, [term]);

  const handleTextChange = (value: string) => {
    setText(value);
    // Replace rather than push, so each keystroke is not a history entry.
    setSearchParams(value.trim() ? { [SEARCH_PARAM]: value } : {}, {
      replace: true
    });
  };

  return (
    <Segment>
      <Form onSubmit={(e) => e.preventDefault()}>
        <Form.Field>
          <label htmlFor="add-organization-search">
            <Translate value={i18nKeys.SEARCH.LABEL} />
          </label>
          <Input
            id="add-organization-search"
            icon="search"
            loading={isLoading || isStale}
            value={text}
            placeholder={I18n.t(i18nKeys.SEARCH.PLACEHOLDER)}
            onChange={(_, { value }) => handleTextChange(value)}
            autoFocus
          />
        </Form.Field>
      </Form>

      {term.length < MIN_SEARCH_LENGTH && (
        <p>{I18n.t(i18nKeys.SEARCH.HINT, { min: MIN_SEARCH_LENGTH })}</p>
      )}

      {error && (
        <Message negative>
          <p>{error}</p>
        </Message>
      )}

      {results && results.length === 0 && !isStale && (
        <p>{I18n.t(i18nKeys.SEARCH.NO_RESULTS, { text: term })}</p>
      )}

      {results && results.length > 0 && (
        <Table
          compact
          celled
          aria-busy={isStale}
          className={isStale ? styles.staleResults : undefined}
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                <Translate value={i18nKeys.SEARCH.COLUMN_NAME} />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <Translate value={i18nKeys.SEARCH.COLUMN_ONBOARDING} />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <Translate value={i18nKeys.SEARCH.COLUMN_EVENTS} />
              </Table.HeaderCell>
              <Table.HeaderCell />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {results.map((organization) => (
              <Table.Row key={organization.id}>
                <Table.Cell>
                  <strong>{organization.name}</strong>
                  {formatOrganizationLocation(organization) && (
                    <div className={styles.muted}>
                      {formatOrganizationLocation(organization)}
                    </div>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {organization.hasPaymentPlatform ? (
                    <Label color="green" basic size="small">
                      <Translate value={i18nKeys.SEARCH.PAYMENT_READY} />
                    </Label>
                  ) : (
                    <Label color="red" basic size="small">
                      <Translate value={i18nKeys.SEARCH.PAYMENT_MISSING} />
                    </Label>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {organization.eventsCount > 0 ? (
                    <Label color="blue" basic size="small">
                      {I18n.t(i18nKeys.SEARCH.EVENTS_COUNT, {
                        count: organization.eventsCount
                      })}
                    </Label>
                  ) : (
                    <Label color="yellow" basic size="small">
                      <Translate value={i18nKeys.SEARCH.NO_EVENTS} />
                    </Label>
                  )}
                </Table.Cell>
                <Table.Cell collapsing>
                  {organization.hasPaymentPlatform && !isStale ? (
                    <Button
                      primary
                      size="small"
                      as={Link}
                      to={organizationEventsPath(organization.id)}
                      state={searchReturnState(searchParams)}
                      aria-label={I18n.t(i18nKeys.SEARCH.SELECT_LABEL, {
                        name: organization.name
                      })}
                    >
                      <Translate value={i18nKeys.SEARCH.SELECT} />
                    </Button>
                  ) : (
                    <Button primary size="small" disabled>
                      <Translate value={i18nKeys.SEARCH.SELECT} />
                    </Button>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </Segment>
  );
};

export default OrganizationSearch;
